import { Button, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { VyIcon } from '@vy/ui-design-system';
import { isEmptyString, isNotNullOrUndefined } from '@vy/utils';
import { useMemo, useState, type FC } from 'react';
import { toast } from 'react-toastify';
import { withSuspenseBoundary } from '../../../../../components/utility/SuspenseBoundary';
import { useAccount } from '../../../../../hooks/requests/use-account';
import { useGetExternalApiToken } from '../../../../../hooks/requests/use-get-external-api-token';
import { useInvalidateExternalApiToken } from '../../../../../hooks/requests/use-invalidate-external-api-token';
import { runMutation } from '../../../../../utils/vy-mutation';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';

const REDUCED_TOKEN_LENGTH = 10;
const HIDDEN_TOKEN_LENGTH = 10;

const ConfigureVayuApiContent: FC = () => {
  const { data, refetch } = useAccount({ suspense: true });
  const existingToken = data?.account.settings.securitySettings?.apiToken;
  const clientId = data?.account.settings.securitySettings?.apiThrottlingKey;

  const invalidateTokenMutation = useInvalidateExternalApiToken({ onSuccess: () => refetch() });
  const getTokenMutation = useGetExternalApiToken({ onSuccess: () => refetch() });

  const mask = '*'.repeat(HIDDEN_TOKEN_LENGTH);
  const [apiToken, setApiToken] = useState<string | undefined>(
    isNotNullOrUndefined(existingToken) ? `${existingToken}${mask}` : undefined,
  );

  const [showRevokeConfirmationDialog, setShowRevokeConfirmationDialog] = useState(false);

  const onCloseRevokeConfirmationDialog = () => {
    setShowRevokeConfirmationDialog(false);
  };

  const onRevokeToken = () => {
    setShowRevokeConfirmationDialog(true);
  };

  const isValidToken = useMemo(
    () => isNotNullOrUndefined(apiToken) && apiToken?.length > REDUCED_TOKEN_LENGTH + HIDDEN_TOKEN_LENGTH,
    [apiToken],
  );

  const isUndefinedToken = useMemo(() => {
    return isEmptyString(apiToken);
  }, [apiToken]);

  const onInvalidate = async () => {
    await runMutation(invalidateTokenMutation, undefined, {
      pending: 'Invalidating token...',
      success: 'Token invalidated',
      error: 'Failed to invalidate token',
    });

    setApiToken('');

    setShowRevokeConfirmationDialog(false);
  };

  const onGetToken = async () => {
    const response = await runMutation(getTokenMutation, undefined, {
      pending: 'Fetching token...',
      success: 'Token fetched',
      error: 'Failed to fetch token',
    });

    setApiToken(response?.refreshToken);
  };

  const onCopyToken = async () => {
    if (isNotNullOrUndefined(apiToken)) {
      await navigator.clipboard.writeText(apiToken);
      toast.success('API token copied to clipboard');
    }
  };

  const onCopyClientId = async () => {
    if (isNotNullOrUndefined(clientId)) {
      await navigator.clipboard.writeText(clientId);
    }
  };

  return (
    <DrawerContainer>
      <VyIcon icon="LogoBlue" />
      <Typography variant="h5">Copy your client id</Typography>
      <Stack
        direction={'row'}
        sx={{
          gap: 1,
          alignItems: 'center'
        }}>
        <TextField fullWidth disabled value={clientId}></TextField>
        <div>
          <IconButton
            style={{
              width: '40px',
              height: '40px',
            }}
            onClick={onCopyClientId}
          >
            <VyIcon
              style={{
                width: '25px',
                height: '25px',
              }}
              icon={'Copy'}
            />
          </IconButton>
        </div>
      </Stack>
      <Typography variant="h5">Fetch your API token</Typography>
      <Stack
        direction={'row'}
        sx={{
          gap: 1,
          alignItems: 'center'
        }}>
        <TextField
          fullWidth
          value={apiToken}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    style={{
                      opacity: isValidToken ? 1 : 0,
                      width: '40px',
                      height: '40px',
                      cursor: 'pointer',
                    }}
                    onClick={onCopyToken}
                    edge="end"
                    disableRipple
                    disabled={!isValidToken}
                  >
                    <VyIcon
                      style={{
                        width: '25px',
                        height: '25px',
                      }}
                      icon="Copy"
                    />
                  </IconButton>
                </InputAdornment>
              ),
              readOnly: true,
            }
          }}
        ></TextField>
        <Stack>
          <Button
            onClick={isUndefinedToken ? onGetToken : onRevokeToken}
            variant="contained"
            color={'secondary'}
            disabled={isValidToken && !isUndefinedToken && !isValidToken}
          >
            {isUndefinedToken ? 'Generate' : 'Revoke'}
          </Button>
          <VyDialog
            open={showRevokeConfirmationDialog}
            onClose={onCloseRevokeConfirmationDialog}
            label={`Revoke API token`}
            labelDescription={`Are you sure you want to revoke your API token?`}
            warningText="Once you revoke the token, it won't be valid anymore and you will need to generate a new one."
            cancelButtonText="Cancel"
            ctaText="Revoke token"
            ctaDisabled={invalidateTokenMutation.isLoading}
            onSubmit={onInvalidate}
          />
        </Stack>
      </Stack>
    </DrawerContainer>
  );
};

const ConfigureVayuApiContentWithSuspenseBoundary = withSuspenseBoundary(ConfigureVayuApiContent);
export { ConfigureVayuApiContentWithSuspenseBoundary as ConfigureVayuApiContent };

const DrawerContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});
